/* eslint-disable ts/no-explicit-any */
/* eslint-disable ts/prefer-nullish-coalescing */
/* eslint-disable ts/no-unsafe-call */
/* eslint-disable ts/no-unsafe-member-access */

import type { BrowserOptions } from '@sentry/react'
import type { Environment } from './environment'
import * as Sentry from '@sentry/react'
import { redactSensitiveData } from '@/shared/libs/redact-sensitive-data'
import {
  getCurrentEnvironment,
  getEnvironmentConfig,
  isProduction,
  isSentryEnabled,
} from './environment'

export interface SentryUser {
  id: string
  email?: string
  username?: string
}

class SentryService {
  private environment: Environment
  private config: ReturnType<typeof getEnvironmentConfig>

  constructor() {
    this.environment = getCurrentEnvironment()
    this.config = getEnvironmentConfig()
  }

  getConfig(): BrowserOptions {
    const sentryConfig = this.config.sentry
    const shouldMaskReplayData = isProduction()
    return {
      dsn: import.meta.env.VITE_SENTRY_DSN,
      // Environment configuration
      environment: this.environment,
      enabled: sentryConfig.enabled,
      sendDefaultPii: true,

      // Performance Monitoring
      tracesSampleRate: sentryConfig.tracesSampleRate,

      // Session Replay
      replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
      replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,

      // Integration configurations
      integrations: [
        Sentry.browserTracingIntegration({
          // Trace TanStack Router navigation
          enableInp: true,
        }),
        Sentry.replayIntegration({
          maskAllText: shouldMaskReplayData, // Mask sensitive text only in production
          blockAllMedia: shouldMaskReplayData, // Block media only in production
        }),
        Sentry.browserProfilingIntegration(),
      ],

      // Release tracking with environment info
      release: `${import.meta.env.VITE_APP_VERSION || 'unknown'}@${this.environment}`,

      // Additional tags for better filtering
      initialScope: {
        tags: {
          environment: this.environment,
          app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
          build_mode: import.meta.env.MODE,
        },
      },

      // Error filtering
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'atomicFindClose',
        // Network errors that are expected
        'NetworkError',
        'Network request failed',
        // Random plugins/extensions
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
      ],

      // PII handling
      beforeSend: (event, hint) => {
        // Log to console in environments where it's enabled
        if (this.config.logToConsole && sentryConfig.enabled) {
          // eslint-disable-next-line no-console
          console.group(`🔴 Sentry Event [${this.environment}]`)
          // eslint-disable-next-line no-console
          console.log('Event:', event)
          // eslint-disable-next-line no-console
          console.log('Hint:', hint)
          // eslint-disable-next-line no-console
          console.log('Will send:', sentryConfig.sendEvents)
          // eslint-disable-next-line no-console
          console.groupEnd()
        }

        // Don't send events if sendEvents is false
        if (!sentryConfig.sendEvents) {
          return null
        }

        // Add environment context to event
        if (event.contexts) {
          event.contexts['environment_info'] = {
            environment: this.environment,
            config: sentryConfig.enabled ? 'enabled' : 'disabled',
            traces_sample_rate: sentryConfig.tracesSampleRate,
          }
        }

        // Scrub sensitive data
        if (event.request?.headers) {
          const headers = event.request.headers as Record<string, any>
          delete headers['Authorization']
          delete headers['Cookie']
          delete headers['X-API-Key']
        }
        if (event.request?.data) {
          event.request.data = redactSensitiveData(event.request.data)
        }
        if (event.extra) {
          event.extra = redactSensitiveData(event.extra)
        }
        if (event.contexts) {
          event.contexts = redactSensitiveData(event.contexts)
        }

        // Add user-friendly event tags for better Sentry dashboard
        if (event.exception?.values?.[0]) {
          const error = event.exception.values[0]
          if (!event.tags) {
            event.tags = {}
          }
          event.tags['error_summary'] = `${error.type || 'Error'}: ${error.value || 'Unknown'}`
        }

        return event
      },

      // Breadcrumb filtering
      beforeBreadcrumb: (breadcrumb) => {
        // Filter out console logs in non-development environments
        if (breadcrumb.category === 'console' && !this.config.logToConsole) {
          return null
        }

        if (breadcrumb.data) {
          breadcrumb.data = redactSensitiveData(breadcrumb.data)
          breadcrumb.data['environment'] = this.environment
        }

        return breadcrumb
      },
    }
  }

  isEnabled(): boolean {
    return isSentryEnabled()
  }

  getEnvironment(): Environment {
    return this.environment
  }

  setUser(user: SentryUser): void {
    if (!this.isEnabled()) return

    const sentryUser: Record<string, string> = { id: user.id }
    if (user.email) sentryUser['email'] = user.email
    if (user.username) sentryUser['username'] = user.username

    Sentry.setUser(sentryUser)
  }

  clearUser(): void {
    if (!this.isEnabled()) return

    Sentry.setUser(null)
  }

  startSpan(name: string, op: string = 'task') {
    if (!this.isEnabled()) return null

    return Sentry.startSpan(
      {
        name,
        op,
      },
      (span) => span,
    )
  }

  endSpan(span: any): void {
    if (!this.isEnabled() || !span) return

    span.end()
  }

  async trackPerformance<T>(name: string, fn: () => Promise<T>): Promise<T> {
    if (!this.isEnabled()) {
      return fn()
    }

    return Sentry.startSpan(
      {
        name,
        op: 'function',
      },
      fn,
    )
  }

  addBreadcrumb(
    category: string,
    message: string,
    data?: Record<string, any>,
    level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  ): void {
    if (!this.isEnabled()) return

    const breadcrumb: {
      category: string
      message: string
      level: 'debug' | 'info' | 'warning' | 'error'
      timestamp: number
      data?: Record<string, any>
    } = {
      category,
      message,
      level,
      timestamp: Date.now() / 1000,
    }

    if (data) {
      breadcrumb.data = data
    }

    Sentry.addBreadcrumb(breadcrumb)
  }

  captureException(error: Error, context?: Record<string, any>): void {
    if (!this.isEnabled()) return

    if (context) {
      Sentry.captureException(error, {
        extra: context,
      })
    } else {
      Sentry.captureException(error)
    }
  }

  captureMessage(
    message: string,
    level: 'debug' | 'info' | 'warning' | 'error' = 'info',
    context?: Record<string, any>,
  ): void {
    if (!this.isEnabled()) return

    if (context) {
      Sentry.captureMessage(message, {
        level,
        extra: context,
      })
    } else {
      Sentry.captureMessage(message, level)
    }
  }

  setContext(name: string, context: Record<string, any>): void {
    if (!this.isEnabled()) return

    Sentry.setContext(name, context)
  }

  setTag(key: string, value: string): void {
    if (!this.isEnabled()) return

    Sentry.setTag(key, value)
  }

  setTags(tags: Record<string, string>): void {
    if (!this.isEnabled()) return

    Sentry.setTags(tags)
  }
}

export const sentryService = new SentryService()

// Export the class for advanced usage or creating custom instances
export { SentryService }
